import { Component } from '@theme/component';
import { ResizeNotifier } from '@theme/utilities';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * The last applied line-clamp value. Used to short-circuit redundant
   * style writes that would otherwise retrigger the ResizeObserver.
   * @type {number}
   */
  #lastMaxLines = -1;

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      // Use ResizeNotifier (not a raw ResizeObserver): it ignores the first
      // callback fired on observe()/re-observe(), so the resize we induce
      // ourselves by writing -webkit-line-clamp does not feed back into the
      // observer. A raw ResizeObserver here creates an infinite
      // write -> reflow -> callback -> write loop that crashes Safari/WebKit
      // ("A problem repeatedly occurred"); Chrome survives only because its
      // ResizeObserver has built-in per-frame loop-limit protection.
      this.resizeObserver = new ResizeNotifier(() => {
        this.#calculateTruncation();
      });

      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize);
      this.#calculateTruncation();
    }
  }

  /**
   * Calculate truncation for the title
   */
  #calculateTruncation = () => {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    const containerHeight = this.clientHeight;
    if (containerHeight <= 0) return;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    // `line-height: normal` parses to NaN; bail out instead of writing "NaN".
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    // Nothing changed -> don't touch the DOM. This is the primary guard that
    // keeps the observer from oscillating.
    if (maxLines === this.#lastMaxLines) return;
    this.#lastMaxLines = maxLines;

    // Stop observing while we mutate styles so our own layout-affecting writes
    // ('-webkit-box' + '-webkit-line-clamp' change the element's height) do not
    // immediately retrigger the observer. ResizeNotifier additionally skips the
    // re-observe's initial fire, fully breaking the feedback loop.
    this.resizeObserver?.disconnect();

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);

    this.resizeObserver?.observe(this);
  };

  /**
   * Handle window resize events
   */
  #handleResize = () => {
    this.#calculateTruncation();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
